import { Close } from "@mui/icons-material";
import { IconButton, Snackbar, SnackbarContent } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import LOCAL_STORAGE from "@/constants/local-storage";
import { RENEW_PERMISSIONS } from "@/constants/modules.permissions";
import {
  defaultPageNumber as pageNumber,
  defaultPageSize as pageSize,
} from "@/constants/pagination";
import ROUTE_URLS from "@/constants/route-urls";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import {
  hasOverduePayment,
  hasPendingPaymentPlan,
  needsPlanRenewal,
} from "@/helpers/customer-sales-product-helpers";
import useGetAllCustomerSalesProducts from "@/hooks/requests/customer-sales-product/use-get-all-customer-sales-product";
import { SidebarMessages } from "@/messages/layout/sidebar";
import customerSalesProductPaymentService from "@/services/customer-sales-product-payment-service";
import { CustomerSalesProductPaymentStatus } from "@/types/customer-sales-product-payment-status.types";
import { FilterType } from "@/types/filter.types";

interface OverduePlanBannerProps {
  messages: SidebarMessages;
}

export const OverduePlanBanner: React.FC<OverduePlanBannerProps> = ({ messages }) => {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { checkPermissions, user } = useAuth();
  const hasNoPermissionToCreate = checkPermissions(RENEW_PERMISSIONS.CREATE);
  const shouldFetchPlans = !hasNoPermissionToCreate && Boolean(user);
  const { data: customerSalesProduct } = useGetAllCustomerSalesProducts({
    pageNumber,
    pageSize,
    enabled: shouldFetchPlans,
  });

  const planItems = customerSalesProduct?.items || [];
  // Só mensagem de pagamento pendente (não usa mais o texto de renovação).
  const shouldShowBanner = hasPendingPaymentPlan(planItems) || needsPlanRenewal(planItems);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    const isBannerClosed = localStorage.getItem(LOCAL_STORAGE.pendingPaymentBannerClosed) === "true";
    setOpen(shouldShowBanner && !hasNoPermissionToCreate && !isBannerClosed);
  }, [shouldShowBanner, hasNoPermissionToCreate]);

  const { pendingPaymentMessagePart1, pendingPaymentMessagePart2, payNowButtonText } = messages;

  const pendingPlan = useMemo(() => {
    const holdPlans = hasOverduePayment(planItems);
    if (holdPlans.length > 0) return holdPlans[0];
    return planItems[0];
  }, [planItems]);

  const handleClose = () => {
    localStorage.setItem(LOCAL_STORAGE.pendingPaymentBannerClosed, "true");
    setOpen(false);
  };

  const handlePayNowClick = async () => {
    if (!pendingPlan) {
      router(ROUTE_URLS.companyPayments(lang), { replace: true });
      handleClose();
      return;
    }

    try {
      const payments = await customerSalesProductPaymentService.filter(
        [
          {
            property: "customerSalesProductId",
            value: pendingPlan.id,
            filterType: FilterType.Equals,
          },
        ],
        { pageNumber: 1, pageSize: 10 },
        "billingDate",
        "desc"
      );

      const unpaidWithInvoice = payments?.items?.find(
        (payment) =>
          !!payment.invoiceUrl &&
          (payment.status === CustomerSalesProductPaymentStatus.BillingSent ||
            payment.status === CustomerSalesProductPaymentStatus.New)
      );

      if (unpaidWithInvoice?.invoiceUrl) {
        window.location.href = unpaidWithInvoice.invoiceUrl;
        return;
      }
    } catch {
      // fallback abaixo
    }

    router(ROUTE_URLS.companyPayments(lang), { replace: true });
    handleClose();
  };

  return (
    <Snackbar
      open={open}
      onClose={(_, reason) => {
        if (reason !== "clickaway") return;
      }}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      autoHideDuration={null}
      sx={({ palette, borders }) => ({
        zIndex: 9999,
        width: { xs: "calc(100% - 32px)", md: "min(1100px, calc(100% - 48px))" },
        maxWidth: "1100px",
        left: "50%",
        right: "auto",
        transform: "translateX(-50%)",
        borderRadius: borders.borderRadius.md,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        background: palette.white.main,
      })}
    >
      <SnackbarContent
        sx={(theme) => ({
          background: theme.palette.white.main,
          color: theme.palette.text.primary,
          width: "100%",
          minWidth: 0,
          flexWrap: "nowrap",
          alignItems: "center",
          padding: theme.spacing(1.5, 2),
          boxShadow: "none",
          "& .MuiSnackbarContent-message": {
            width: "100%",
            padding: 0,
            flex: "1 1 auto",
            minWidth: 0,
          },
        })}
        message={
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="nowrap"
            gap={2}
            width="100%"
            sx={({ breakpoints }) => ({
              [breakpoints.down("md")]: {
                flexDirection: "column",
                flexWrap: "wrap",
                alignItems: "stretch",
                gap: 1.5,
              },
            })}
          >
            <MDTypography
              variant="body2"
              sx={{
                flex: "1 1 auto",
                minWidth: "max-content",
                whiteSpace: { xs: "normal", md: "nowrap" },
                textAlign: { xs: "center", md: "left" },
                pr: { md: 1 },
              }}
            >
              <strong>{pendingPaymentMessagePart1}</strong>
              {pendingPaymentMessagePart2}
            </MDTypography>

            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}
              flexShrink={0}
              sx={({ breakpoints }) => ({
                [breakpoints.down("md")]: {
                  justifyContent: "center",
                },
              })}
            >
              <MDButton
                variant="gradient"
                color="dark"
                onClick={handlePayNowClick}
                sx={{ whiteSpace: "nowrap", minWidth: "auto", px: 2.5 }}
              >
                {payNowButtonText}
              </MDButton>

              <IconButton aria-label="close" onClick={handleClose} size="small">
                <Close fontSize="small" />
              </IconButton>
            </MDBox>
          </MDBox>
        }
      />
    </Snackbar>
  );
};
